import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PATH } from '../widget-lib/path-routes';
import { PromoBanner } from './promo-banner';
import { useEmbedBootstrap } from '../use-widget-bootstrap';

import { formatAccessTime } from '../widget-lib/access-code-format';

type QRCodeData = {
  pin: string;
  qr_data_url: string | null;
  lesson_name: string;
  starts_at: string;
  ends_at: string;
};

type BookingSuccessModalProps = {
  open: boolean;
  onClose: () => void;
  qrCode: QRCodeData | null;
  loading: boolean;
  isPaymentRequired: boolean;
};

export function BookingSuccessModal({ open, onClose, qrCode, loading, isPaymentRequired }: BookingSuccessModalProps) {
  const [searchParams] = useSearchParams();
  const { effectiveModules, me } = useEmbedBootstrap();
  const showQrFeature = (effectiveModules ?? me?.modules)?.qrcode === true;
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-sm rounded-2xl bg-white dark:bg-[var(--bg-elevated)] p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          {loading ? (
            <div className="py-8">
              <div className="mx-auto h-16 w-16 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Получаем детали записи...</p>
            </div>
          ) : (
            <>
              <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
                <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Запись создана!</h3>
              <PromoBanner screen="success" />
              {qrCode ? (
                <>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{qrCode.lesson_name}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {formatAccessTime(qrCode.starts_at, qrCode.ends_at)}
                  </p>
                </>
              ) : null}

              {isPaymentRequired ? (
                <div className="mt-4 rounded-xl bg-yellow-50 dark:bg-yellow-950/40 p-4 text-sm text-yellow-800 dark:text-yellow-200">
                  Оплатите занятие в студии наличными.
                </div>
              ) : null}

              {showQrFeature ? (
                <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-3 text-[13px] text-emerald-800 dark:text-emerald-200 space-y-1">
                  <p className="font-semibold flex items-center justify-center gap-1.5">
                    <span>📱</span>
                    <span>Доступ по единому динамическому QR-коду</span>
                  </p>
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    На турникете сканируйте ваш живой 30-секундный QR-код из раздела «QR-коды» или используйте Яндекс.Ключ.
                  </p>
                </div>
              ) : null}

              <div className="mt-6 flex flex-col gap-2">
                {showQrFeature ? (
                  <Link
                    to={`${PATH.myAccess}${suffix}`}
                    className="inline-flex h-10 items-center justify-center rounded-lg bg-[var(--accent-bg)] px-5 text-sm font-medium text-[var(--accent-fg)] hover:bg-[var(--accent-bg-hover)]"
                    onClick={onClose}
                  >
                    📱 Мой динамический QR-код
                  </Link>
                ) : null}
                <Link
                  to={`${PATH.bookings}${suffix}`}
                  className={`inline-flex h-10 items-center justify-center rounded-lg px-5 text-sm font-medium ${
                    showQrFeature
                      ? 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      : 'bg-[var(--accent-bg)] text-[var(--accent-fg)] hover:bg-[var(--accent-bg-hover)]'
                  }`}
                  onClick={onClose}
                >
                  Мои записи
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
